import { createSlice } from '@reduxjs/toolkit'

const initialState = {
  isLoading: false,
  notifications: [],
  theme: 'dark',
  isMobile: false,
  sidebarOpen: false,
  errors: {
    global: null,
    network: false,
    lastError: null
  }
}

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },
    
    addNotification: (state, action) => {
      const notification = {
        id: Date.now() + Math.random(),
        type: 'info',
        title: '',
        message: '',
        duration: 5000,
        ...action.payload,
      }
      state.notifications.push(notification)
    },
    
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      )
    },
    
    clearNotifications: (state) => {
      state.notifications = []
    },
    
    setTheme: (state, action) => {
      state.theme = action.payload
      document.documentElement.setAttribute('data-theme', action.payload)
      document.documentElement.classList.toggle('dark', action.payload === 'dark')
      localStorage.setItem('portfolio-theme', action.payload)
    },
    
    toggleTheme: (state) => {
      const newTheme = state.theme === 'dark' ? 'light' : 'dark'
      state.theme = newTheme
      document.documentElement.setAttribute('data-theme', newTheme)
      document.documentElement.classList.toggle('dark', newTheme === 'dark')
      localStorage.setItem('portfolio-theme', newTheme)
    },
    
    setMobile: (state, action) => {
      state.isMobile = action.payload
    },
    
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload
    },
    
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },

    setGlobalError: (state, action) => {
      state.errors.global = action.payload
      state.errors.lastError = {
        error: action.payload,
        timestamp: Date.now()
      }
    },

    clearGlobalError: (state) => {
      state.errors.global = null
    },

    setNetworkError: (state, action) => {
      state.errors.network = action.payload
    },

    clearAllErrors: (state) => {
      state.errors.global = null
      state.errors.network = false
    },
  },
})

export const {
  setLoading,
  addNotification,
  removeNotification,
  clearNotifications,
  setTheme,
  toggleTheme,
  setMobile,
  setSidebarOpen,
  toggleSidebar,
  setGlobalError,
  clearGlobalError,
  setNetworkError,
  clearAllErrors,
} = appSlice.actions

// Thunk для инициализации приложения
export const initializeApp = () => (dispatch) => {
  // Загружаем тему из localStorage
  const savedTheme = localStorage.getItem('portfolio-theme')
  if (savedTheme) {
    dispatch(setTheme(savedTheme))
  }
  
  // Определяем мобильное устройство
  const checkMobile = () => {
    dispatch(setMobile(window.innerWidth <= 768))
  }
  
  checkMobile()
  window.addEventListener('resize', checkMobile)
  
  // Показываем уведомление о готовности
  dispatch(addNotification({
    type: 'success',
    title: 'Добро пожаловать!',
    message: 'Портфолио загружено успешно',
    duration: 3000
  }))
}

// Утилиты для уведомлений
export const showSuccess = (message, title = 'Успешно') => (dispatch) => {
  dispatch(addNotification({ type: 'success', title, message }))
}

export const showError = (message, title = 'Ошибка') => (dispatch) => {
  dispatch(addNotification({ type: 'error', title, message, duration: 8000 }))
}

export const showWarning = (message, title = 'Внимание') => (dispatch) => {
  dispatch(addNotification({ type: 'warning', title, message }))
}

export const showInfo = (message, title = 'Информация') => (dispatch) => {
  dispatch(addNotification({ type: 'info', title, message }))
}

export const showNetworkError = () => (dispatch) => {
  dispatch(setNetworkError(true))
  dispatch(addNotification({
    type: 'error',
    title: 'Проблемы с сетью',
    message: 'Проверьте подключение к интернету',
    duration: 0, // Постоянное уведомление
    persistent: true
  }))
}

export default appSlice.reducer